import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import PostAssetDialog from '../components/PostAssetDialog';
import axios from 'axios';
import { 
  Download, 
  Copy, 
  ChevronLeft, 
  Check,
  Loader2,
  SplitSquareHorizontal,
  Share2,
  Image as ImageIcon,
  RefreshCw,
  Heart,
  Code
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssetEditor = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [copiedPlatform, setCopiedPlatform] = useState(null);
  const [copiedCTA, setCopiedCTA] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const [videoStatus, setVideoStatus] = useState('pending');
  const [activeTab, setActiveTab] = useState('slider');

  useEffect(() => {
    fetchAsset();
  }, [assetId]);

  // Poll for video status if pending or rendering
  useEffect(() => {
    if (videoStatus === 'rendering' || videoStatus === 'pending') {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/api/assets/${assetId}/video/status`);
          const newStatus = response.data.status;
          setVideoStatus(newStatus);
          if (newStatus === 'ready' || newStatus === 'failed') {
            clearInterval(interval);
            if (newStatus === 'ready') {
              fetchAsset(); // Refresh full asset data to get video paths
            }
          }
        } catch (error) {
          console.error('Error checking video status:', error);
        }
      }, 2000); // Poll every 2 seconds
      return () => clearInterval(interval);
    }
  }, [videoStatus, assetId]);

  const fetchAsset = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assets/${assetId}`);
      setAsset(response.data);
      setVideoStatus(response.data.video_status || 'pending');
    } catch (error) {
      toast.error('Failed to load asset');
      navigate('/library');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, platform) => {
    try {
      await navigator.clipboard.writeText(text);
      if (platform === 'cta') {
        setCopiedCTA(true);
        setTimeout(() => setCopiedCTA(false), 2000);
      } else {
        setCopiedPlatform(platform);
        setTimeout(() => setCopiedPlatform(null), 2000);
      }
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const downloadVideo = async () => {
    if (!asset?.video_path || videoStatus !== 'ready') return;
    
    setDownloadingVideo(true);
    try {
      const response = await axios.get(`${API_URL}/api/assets/${assetId}/video/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salesassets_feed_${assetId.slice(0, 8)}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Feed video downloaded!');
    } catch (error) {
      toast.error('Failed to download video');
    } finally {
      setDownloadingVideo(false);
    }
  };

  const downloadStoryVideo = async () => {
    if (!asset?.video_path_story || videoStatus !== 'ready') return;
    
    try {
      const response = await axios.get(`${API_URL}/api/assets/${assetId}/video/download-story`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salesassets_story_${assetId.slice(0, 8)}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Story video downloaded!');
    } catch (error) {
      toast.error('Failed to download story video');
    }
  };

  const regenerateVideo = async () => {
    try {
      await axios.post(`${API_URL}/api/assets/${assetId}/regenerate-video`);
      setVideoStatus('rendering');
      toast.success('Video regeneration started');
    } catch (error) {
      toast.error('Failed to regenerate video');
    }
  };

  const shareToSocial = async () => {
    // Check if Web Share API is available (mobile browsers)
    if (!navigator.share) {
      toast.error('Sharing not supported on this device. Please download the video instead.');
      return;
    }

    try {
      // Fetch the video as a blob
      const response = await axios.get(`${API_URL}/api/assets/${assetId}/video/download`, {
        responseType: 'blob'
      });
      
      const videoBlob = new Blob([response.data], { type: 'video/mp4' });
      const videoFile = new File([videoBlob], `transformation.mp4`, { type: 'video/mp4' });
      
      // Get the copy text
      const copyText = asset.generated_copy?.platform_copy?.facebook_instagram || 
                       asset.generated_copy?.cta || 
                       '';

      // Copy text to clipboard first (so it's ready to paste if the app doesn't accept it)
      if (copyText) {
        try {
          await navigator.clipboard.writeText(copyText);
        } catch (e) {
          // Clipboard might not be available, continue anyway
        }
      }

      // Check if we can share files
      if (navigator.canShare && navigator.canShare({ files: [videoFile] })) {
        await navigator.share({
          files: [videoFile],
          title: 'Check out this transformation',
          text: copyText
        });
        toast.success('Caption copied to clipboard. Paste it when posting!');
      } else {
        // Fallback: share just the text
        await navigator.share({
          title: 'Check out this transformation',
          text: copyText
        });
        toast.info('Shared text. Download the video to attach it.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    }
  };

  const shareStoryVideo = async () => {
    if (!navigator.share) {
      toast.error('Sharing not supported on this device. Please download the video instead.');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/assets/${assetId}/video/download-story`, {
        responseType: 'blob'
      });
      
      const videoBlob = new Blob([response.data], { type: 'video/mp4' });
      const videoFile = new File([videoBlob], `story.mp4`, { type: 'video/mp4' });
      
      // Use story copy if available, otherwise fall back to regular copy
      const copyText = asset.generated_copy?.story_copy?.facebook_instagram || 
                       asset.generated_copy?.cta || 
                       '';

      // Copy text to clipboard first (so it's ready to paste if the app doesn't accept it)
      if (copyText) {
        try {
          await navigator.clipboard.writeText(copyText);
        } catch (e) {
          // Clipboard might not be available, continue anyway
        }
      }

      if (navigator.canShare && navigator.canShare({ files: [videoFile] })) {
        await navigator.share({
          files: [videoFile],
          title: 'Story',
          text: copyText
        });
        toast.success('Caption copied to clipboard. Paste it when posting!');
      } else {
        await navigator.share({
          title: 'Story',
          text: copyText
        });
        toast.info('Shared text. Download the video to attach it.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    }
  };

  const copyEmbedCode = async () => {
    const embedUrl = `https://salesassets.co/embed/${assetId}`;
    const embedCode = `<iframe src="${embedUrl}" width="400" height="400" frameborder="0" style="border-radius: 12px; max-width: 100%;"></iframe>`;
    
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopiedEmbed(true);
      toast.success('Embed code copied');
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0a2540]" />
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <div className="pt-24 text-center">
          <p className="text-slate-600">Asset not found</p>
        </div>
      </div>
    );
  }

  // Image URLs
  const sliderBefore = asset.slider_images?.before 
    ? `${API_URL}${asset.slider_images.before}`
    : asset.images?.[0]?.startsWith('http') 
      ? asset.images[0] 
      : `${API_URL}${asset.images?.[0] || ''}`;
      
  const sliderAfter = asset.slider_images?.after 
    ? `${API_URL}${asset.slider_images.after}`
    : asset.images?.[asset.images.length - 1]?.startsWith('http') 
      ? asset.images[asset.images.length - 1] 
      : `${API_URL}${asset.images?.[asset.images.length - 1] || ''}`;

  const videoUrl = asset.video_path ? `${API_URL}${asset.video_path}` : null;
  const platformCopy = asset.generated_copy?.platform_copy;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />
      
      <div className="pt-20 px-4 pb-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/library')}
              className="text-slate-600"
              data-testid="back-to-library-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to library
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Previews */}
            <div className="space-y-4">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="slider" className="flex items-center gap-2">
                    <SplitSquareHorizontal className="w-4 h-4" />
                    Interactive Slider
                  </TabsTrigger>
                  <TabsTrigger value="gif" className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Before & After Video
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="slider" className="mt-4">
                  <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                    <BeforeAfterSlider
                      beforeImage={sliderBefore}
                      afterImage={sliderAfter}
                      aspectRatio="1/1"
                    />
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <Button
                        onClick={copyEmbedCode}
                        variant="outline"
                        className="w-full"
                        data-testid="copy-embed-btn"
                      >
                        {copiedEmbed ? (
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                        ) : (
                          <Code className="w-4 h-4 mr-2" />
                        )}
                        {copiedEmbed ? 'Copied!' : 'Copy embed code for your website'}
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="gif" className="mt-4">
                  <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                    {videoStatus === 'rendering' && (
                      <div className="aspect-square bg-slate-100 rounded-xl flex flex-col items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-[#0a2540] mb-3" />
                        <p className="text-slate-600 font-medium">Creating your video...</p>
                        <p className="text-sm text-slate-400 mt-1">Feed + Story versions</p>
                      </div>
                    )}
                    
                    {videoStatus === 'failed' && (
                      <div className="aspect-square bg-red-50 rounded-xl flex flex-col items-center justify-center">
                        <p className="text-red-600 font-medium">Video generation failed</p>
                        <Button
                          onClick={regenerateVideo}
                          variant="outline"
                          className="mt-3"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Try Again
                        </Button>
                      </div>
                    )}
                    
                    {videoStatus === 'ready' && videoUrl && (
                      <div className="space-y-6">
                        {/* Feed Post Video */}
                        <div className="bg-slate-50 rounded-xl p-4">
                          <h4 className="font-semibold text-[#0a2540] mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-[#0a2540] text-white text-xs rounded flex items-center justify-center">1:1</span>
                            Feed Post
                          </h4>
                          <video 
                            src={videoUrl} 
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full aspect-square object-cover rounded-lg mb-3"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={shareToSocial}
                              className="flex-1 bg-[#0a2540] text-white font-semibold hover:bg-[#0a2540]/90"
                              data-testid="share-video-btn"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Share
                            </Button>
                            <Button
                              onClick={downloadVideo}
                              disabled={downloadingVideo}
                              variant="outline"
                              className="px-3"
                              data-testid="download-video-btn"
                            >
                              {downloadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* Story Video */}
                        {asset.video_path_story && (
                          <div className="bg-slate-50 rounded-xl p-4">
                            <h4 className="font-semibold text-[#0a2540] mb-3 flex items-center gap-2">
                              <span className="w-6 h-6 bg-[#d4af37] text-[#0a2540] text-xs rounded flex items-center justify-center">9:16</span>
                              Story
                            </h4>
                            <div className="flex gap-4">
                              <video 
                                src={`${API_URL}${asset.video_path_story}`}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-32 aspect-[9/16] object-cover rounded-lg"
                              />
                              <div className="flex-1 flex flex-col justify-center">
                                <p className="text-sm text-slate-600 mb-3">
                                  Vertical format for Instagram & Facebook Stories
                                </p>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => shareStoryVideo()}
                                    className="flex-1 bg-[#d4af37] text-[#0a2540] font-semibold hover:bg-[#c5a028]"
                                    data-testid="share-story-btn"
                                  >
                                    <Share2 className="w-4 h-4 mr-2" />
                                    Share
                                  </Button>
                                  <Button
                                    onClick={() => downloadStoryVideo()}
                                    variant="outline"
                                    className="px-3"
                                    data-testid="download-story-btn"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <Button
                          onClick={regenerateVideo}
                          variant="outline"
                          className="w-full"
                          data-testid="regenerate-video-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regenerate Videos
                        </Button>
                      </div>
                    )}
                    
                    {videoStatus === 'pending' && (
                      <div className="aspect-square bg-slate-100 rounded-xl flex flex-col items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-[#0a2540] mb-3" />
                        <p className="text-slate-600">Before & After Video will be ready shortly</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Right Column - Copy */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h2 className="font-bold text-[#0a2540]">Social Media Copy</h2>
                  <p className="text-sm text-slate-500">Click to copy, then paste when posting</p>
                </div>
                
                <div className="divide-y divide-slate-100">
                  {/* Facebook/Instagram */}
                  {platformCopy?.facebook_instagram && (
                    <div 
                      onClick={() => copyToClipboard(platformCopy.facebook_instagram, 'fb')}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-[#0a2540]">
                          <span className="inline-block w-5 h-5 bg-[#0a2540] text-white text-[10px] rounded mr-2 text-center leading-5">1:1</span>
                          Feed Post
                        </span>
                        {copiedPlatform === 'fb' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-line">{platformCopy.facebook_instagram}</p>
                    </div>
                  )}

                  {/* Story Copy */}
                  {asset.generated_copy?.story_copy?.facebook_instagram && (
                    <div 
                      onClick={() => copyToClipboard(asset.generated_copy.story_copy.facebook_instagram, 'story')}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors bg-[#d4af37]/5"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-[#0a2540]">
                          <span className="inline-block w-5 h-5 bg-[#d4af37] text-[#0a2540] text-[10px] rounded mr-2 text-center leading-5">9:16</span>
                          Story
                        </span>
                        {copiedPlatform === 'story' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-line">{asset.generated_copy.story_copy.facebook_instagram}</p>
                    </div>
                  )}
                  
                  {/* Ads Copy */}
                  {platformCopy?.ads && (
                    <div 
                      onClick={() => copyToClipboard(platformCopy.ads, 'ads')}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-[#0a2540]">Facebook / Instagram Ads</span>
                        {copiedPlatform === 'ads' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-3">{platformCopy.ads}</p>
                    </div>
                  )}
                  
                  {/* Google Business */}
                  {platformCopy?.google_business && (
                    <div 
                      onClick={() => copyToClipboard(platformCopy.google_business, 'gbp')}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-[#0a2540]">Google Business Profile</span>
                        {copiedPlatform === 'gbp' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-3">{platformCopy.google_business}</p>
                    </div>
                  )}
                  
                  {/* Website */}
                  {platformCopy?.website && (
                    <div 
                      onClick={() => copyToClipboard(platformCopy.website, 'web')}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-[#0a2540]">Website / Blog</span>
                        {copiedPlatform === 'web' ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-3">{platformCopy.website}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* CTA */}
              {asset.generated_copy?.cta && (
                <div 
                  onClick={() => copyToClipboard(asset.generated_copy.cta, 'cta')}
                  className="bg-[#0a2540] text-white rounded-2xl p-4 cursor-pointer hover:bg-[#0a2540]/90 transition-colors"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-white/70">Call to Action</span>
                    {copiedCTA ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-white/50" />
                    )}
                  </div>
                  <p className="font-medium">{asset.generated_copy.cta}</p>
                </div>
              )}

              {/* Soft Referral CTA */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-600">
                      Like your SalesAssets experience? 
                      <Link 
                        to="/share" 
                        className="text-[#0a2540] font-medium hover:underline ml-1"
                        data-testid="referral-soft-cta"
                      >
                        Share this app with your mates
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Post Dialog */}
      <PostAssetDialog
        isOpen={showPostDialog}
        onClose={() => setShowPostDialog(false)}
        asset={asset}
        caption={platformCopy?.facebook_instagram || ''}
        cta={asset.generated_copy?.cta || ''}
      />
    </div>
  );
};

export default AssetEditor;
